import { getFirestore, collection, query, where, orderBy, limit, startAfter, documentId, onSnapshot, doc, updateDoc, getDoc, setDoc, getCountFromServer } from 'firebase/firestore';
import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { decryptDisplayName, decryptDisplayNames, searchDisplayNames } from '../shared/utils/encryptionUtils';

const usersDbName = `${import.meta.env.VITE_USERS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`
const operationsDbName = `${import.meta.env.VITE_OPERATIONS_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`
const errorDbName = `${import.meta.env.VITE_ERROR_DB_PREFIX}-${import.meta.env.VITE_LOCATION_SHORTHAND}`

const PATIENTS_COLLECTION = 'patients';

/** @param {any} data */
function docCreatedTimeMillis(data) {
  return data.createdTime ? (data.createdTime.toMillis ? data.createdTime.toMillis() : data.createdTime) : 0;
}

/** @param {any} data */
function docExamDate(data) {
  return typeof data.exam_date === 'string' ? data.exam_date : null;
}

/**
 * Subscribe to one page of a user's patients, ordered by createdTime desc.
 * Live (onSnapshot), so a newly-uploaded patient appears immediately on
 * page 1. Returns an unsubscribe function - unsubscribe before subscribing
 * to a different page/page size (a new query is a new listener).
 *
 * @param {string} uid - user id to filter patients by (user_id field); demo callers pass 'demo'
 * @param {{ pageSize: number, cursor?: import('firebase/firestore').QueryDocumentSnapshot | null }} pageParams
 * @param {(page: { rows: Array<{ patientId: string, displayName: string, createdTime: number, examDate: string | null }>, lastDoc: import('firebase/firestore').QueryDocumentSnapshot | null, hasMore: boolean }) => void} onUpdate
 * @param {(err: any)=>void} [onError]
 * @param {string} [dbName]
 * @returns {() => void} unsubscribe
 */
export function subscribeToUserPatients(uid, { pageSize, cursor = null }, onUpdate, onError, dbName = operationsDbName) {
  const db = getFirestore(dbName);
  const baseQuery = query(
    collection(db, PATIENTS_COLLECTION),
    where('user_id', '==', uid),
    orderBy('createdTime', 'desc'),
    // fetch one extra row to detect whether a next page exists
    limit(pageSize + 1)
  );
  const q = cursor ? query(baseQuery, startAfter(cursor)) : baseQuery;

  let latestSeq = 0;
  const unsubscribe = onSnapshot(q, { includeMetadataChanges: true }, async (querySnapshot) => {
    const seq = ++latestSeq;
    try {
      /** @type {Array<import('firebase/firestore').QueryDocumentSnapshot>} */
      const docs = [];
      querySnapshot.forEach((d) => {
        if (!d.metadata.hasPendingWrites) docs.push(d);
      });

      const hasMore = docs.length > pageSize;
      const pageDocs = hasMore ? docs.slice(0, pageSize) : docs;
      const lastDoc = pageDocs.length > 0 ? pageDocs[pageDocs.length - 1] : null;

      const withDisplayName = pageDocs.filter((d) => d.data().display_name);
      const decrypted = await decryptDisplayNames(
        withDisplayName.map((d) => ({ patientId: d.data().patient_id, ciphertext: d.data().display_name }))
      );

      const rows = pageDocs.map((d) => {
        const data = d.data();
        const displayName = !data.display_name
          ? 'Anonymous'
          : (decrypted[data.patient_id] ?? '[name unavailable]');
        return {
          patientId: data.patient_id,
          displayName,
          createdTime: docCreatedTimeMillis(data),
          examDate: docExamDate(data),
        };
      });

      if (seq !== latestSeq) return;
      onUpdate({ rows, lastDoc, hasMore });
    } catch (err) {
      // onSnapshot does not await async callbacks — errors must be routed manually
      if (onError) onError(err);
      else console.error('subscribeToUserPatients error', err);
    }
  }, (err) => {
    if (onError) onError(err);
    else console.error('subscribeToUserPatients error', err);
  });

  return unsubscribe;
}

/**
 * Total patient count for a user, for the DataGrid's server-pagination
 * footer. Call once per subscription (re)initialization, not per page/keystroke.
 * @param {string} uid
 * @param {string} [dbName]
 * @returns {Promise<number>}
 */
export async function countUserPatients(uid, dbName = operationsDbName) {
  const db = getFirestore(dbName);
  const q = query(collection(db, PATIENTS_COLLECTION), where('user_id', '==', uid));
  const snapshot = await getCountFromServer(q);
  return snapshot.data().count;
}

/**
 * Server-side name search over the caller's own patients (or the shared
 * demo record for demo callers) - reshapes encryptionUtils.searchDisplayNames
 * into the same row shape the patient list grid already uses.
 * @param {string} term
 * @returns {Promise<{ rows: Array<{ patientId: string, displayName: string, createdTime: number, examDate: string | null }>, truncated: boolean }>}
 */
export async function searchUserPatients(term) {
  const { results, truncated } = await searchDisplayNames(term);
  const rows = results.map((match) => ({
    patientId: match.patientId,
    displayName: match.displayName,
    createdTime: match.createdTime ?? 0,
    examDate: match.examDate ?? null,
  }));
  return { rows, truncated };
}

/**
 * Subscribe to the current user's document (users/{uid}).
 * Returns an unsubscribe function.
 *
 * @param {string} uid
 * @param {(userDoc: object|null)=>void} onUpdate - receives doc.data() or null
 * @param {(err: any)=>void} [onError]
 * @returns {() => void}
 */
export function subscribeToCurrentUserDoc(uid, onUpdate, onError) {
  const db = getFirestore(usersDbName);
  const userDocRef = doc(db, 'users', uid);
  const unsubscribe = onSnapshot(userDocRef, (docSnapshot) => {
    onUpdate(docSnapshot.exists() ? docSnapshot.data() : null);
  }, (err) => {
    if (onError) onError(err);
    else console.error('subscribeToCurrentUserDoc error', err);
  });

  return unsubscribe;
}

/**
 * Set the selected patient for a user (updates users/{uid}.patientId).
 * Returns a Promise that resolves when update completes.
 * This is the canonical API used when a patient is selected from the UI.
 *
 * @param {string} uid
 * @param {string} patientId
 */
export async function selectPatientForUser(uid, patientId) {
  if (!uid) throw new Error('missing uid');
  if (!patientId) throw new Error('missing patientId');

  const db = getFirestore(usersDbName);
  const userDocRef = doc(db, 'users', uid);
  await setDoc(userDocRef, { patientId }, { merge: true });
}

/**
 * Subscribe to the progress document for a given patient.
 * `display_name` is decrypted before being passed to onUpdate.
 * If decryption fails or user_id is missing, display_name is set to '[name unavailable]'
 * and _nameDecryptFailed is set to true on the data object — onUpdate is still called.
 * Returns an unsubscribe function.
 *
 * @param {string} patientId - id of the patient to watch
 * @param {(progress: object|null)=>void} onUpdate - receives document data (with decrypted display_name) or null
 * @param {(err: any)=>void} [onError]
 * @param {string} [dbName] - optional named Firestore instance
 * @returns {() => void} unsubscribe
 */
export function subscribeToPatientProgress(patientId, onUpdate, onError, dbName = operationsDbName) {
  const db = getFirestore(dbName);
  const docRef = doc(db, 'patients', patientId);
  let latestSeq = 0;
  const unsubscribe = onSnapshot(docRef, async (docSnapshot) => {
    const seq = ++latestSeq;
    try {
      if (!docSnapshot.exists()) {
        if (seq === latestSeq) onUpdate(null);
        return;
      }
      const data = { ...docSnapshot.data() };
      if (data.display_name) {
        try {
          // onSnapshot does not await async callbacks - decryption errors must be caught here
          data.display_name = await decryptDisplayName(patientId, data.display_name);
        } catch {
          data.display_name = '[name unavailable]';
          data._nameDecryptFailed = true;
        }
      }
      if (seq !== latestSeq) return;
      onUpdate(data);
    } catch (err) {
      if (onError) onError(err);
      else console.error('subscribeToPatientProgress error', err);
    }
  }, (err) => {
    if (onError) onError(err);
    else console.error('subscribeToPatientProgress error', err);
  });

  return unsubscribe;
}

/**
 * Fetch a patient progress document from the operations-progress database.
 *
 * @param {string} patientId
 * @param {string} [dbName='visionerves-operations-progress-db']
 * @returns {Promise<object|null>}
 */
export async function getPatientProgress(patientId, dbName = operationsDbName) {
  if (!patientId) throw new Error('getPatientProgress: missing patientId');

  const db = getFirestore(dbName);
  const patientRef = doc(db, 'patients', patientId);
  const snapshot = await getDoc(patientRef);

  return snapshot.exists() ? snapshot.data() : null;
}

/**
 * Subscribe to the error collection for a given patient.
 * Returns an unsubscribe function.
 *
 * @param {string} patientId - id of the patient to watch for error docs
 * @param {(hasError: boolean, errorType: string)=>void} onUpdate - receives true if any error documents exist
 * @param {(err: any)=>void} [onError]
 * @param {string} [dbName='import.meta.env.VITE_FIRESTORE_ERROR_DB'] - optional named Firestore instance
 * @returns {() => void} unsubscribe
 */
export function subscribeToPatientErrors(patientId, onUpdate, onError, dbName = errorDbName) {
  const db = getFirestore(dbName);
  const q = query(collection(db, 'patients'), where(documentId(), '==', patientId));

  const unsubscribe = onSnapshot(q, (querySnapshot) => {
    if (!querySnapshot.empty) {
      const errorType = querySnapshot.docs[0].get('error_type');
      errorType === undefined ? 'processing' : errorType;
      onUpdate(true, errorType);
    } else {
      onUpdate(false, '');
    }
  }, (err) => {
    if (onError) onError(err);
    else console.error('subscribeToPatientErrors error', err);
  });

  return unsubscribe;
}

export async function patientImagesUploaded(userId, patientId) {
  const app = getApp();
  const functions = getFunctions(app, import.meta.env.VITE_LOCATION);
  const uploadHandler = httpsCallable(functions, 'uploadHandler');
  const res = await uploadHandler({user_id: userId, patient_id: patientId});
}

